import type { OtimAPIClient } from "@api/api-client";
import type { AuthLoginResponse, AuthLogoutResponse } from "@api/schemas/auth";
import type { Address, WalletClient } from "viem";

import { createSiweMessage } from "viem/siwe";

import { parseSignatureToVRS } from "@utils/helpers/misc";

export interface LoginOptions {
  domain: string;
  uri: string;
  address: Address;
  chainId: number;
}

export async function login(
  client: OtimAPIClient,
  walletClient: WalletClient,
  { domain, uri, address, chainId }: LoginOptions,
): Promise<AuthLoginResponse> {
  if (!walletClient.account) {
    throw new Error("No account connected to wallet client");
  }

  const message = createSiweMessage({
    domain,
    uri,
    address,
    statement:
      "Welcome to Otim! By signing in, you accept the Otim Terms and Conditions (https://otim.com/tac). This request will not trigger a blockchain transaction or cost any gas fees.",
    version: "1",
    chainId,
    nonce: Date.now().toString(),
    issuedAt: new Date(),
  });

  const signature = await walletClient.signMessage({
    account: walletClient.account!,
    message,
  });

  const vrsParsedSignature = parseSignatureToVRS(signature);

  const response = await client.auth.login({
    siwe: message,
    signature: vrsParsedSignature,
  });

  return response.data;
}

export async function logout(
  client: OtimAPIClient,
): Promise<AuthLogoutResponse> {
  const response = await client.auth.logout();
  client.clearAuth();
  return response.data;
}

export async function setAuthorizationHeader(
  client: OtimAPIClient,
  authorizationToken: string,
): Promise<void> {
  if (!authorizationToken || typeof authorizationToken !== "string") {
    throw new Error("Authorization token must be a non-empty string");
  }

  client.clearAuth();
  client.setAuthToken(authorizationToken);
}

export interface AuthClient {
  readonly login: (options: LoginOptions) => ReturnType<typeof login>;
  readonly logout: () => ReturnType<typeof logout>;
  readonly setAuthorizationHeader: (
    authorizationToken: string,
  ) => ReturnType<typeof setAuthorizationHeader>;
}

export function createAuthClient(
  client: OtimAPIClient,
  walletClient: WalletClient,
): AuthClient {
  return {
    login: (options: LoginOptions) => login(client, walletClient, options),
    logout: () => logout(client),
    setAuthorizationHeader: (authorizationToken: string) =>
      setAuthorizationHeader(client, authorizationToken),
  };
}
